import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import {
  BenefitDTOBenefitEnum,
  BulkActivationResultItemDTO,
  BulkActivationUpdateResponseDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogState } from '../../../component/dialog';
import { TabbedDialog, TabbedDialogTab } from '../../../component/dialog/TabbedDialog';
import { BenefitActivationDialogExtensionInterface } from '../../../extension/interface/benefit-activation-dialog.extension.interface';
import { useBenefitContext } from '../BenefitContext';
import { enqueueBulkResultSnackbar } from '../bulkActivation/bulkResultSnackbar';
import { getExtensionForBenefit } from '../index';
import { BulkActivationResultDialog } from './BulkActivationResultDialog';
import { BulkEditForm } from './BulkEditForm';
import { BulkErrorDialog } from './BulkErrorDialog';
import { DeactivateForm } from './DeactivateForm';
import { PauseForm } from './PauseForm';
import { SingleActivationResultDialog } from './SingleActivationResultDialog';
import { useBoundActivationComponent } from './useBoundActivationComponent';
import { PauseMutationDTO } from './useBulkPauseActivationsMutation';

interface Props {
  dialogState: DialogState;
  translationPrefix: string;
  isBulkMode?: boolean;
  terminationDate?: DateTime;
  onDeactivate?: (dto: UpdateBenefitActivationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onPause?: (dto: PauseMutationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onUpdate?: (dto: UpdateBenefitActivationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onSuccessReset?: () => void;
  updateProgress?: number;
  pauseProgress?: number;
  disabled?: boolean;
}

export const ActivationActionsDialog: React.FC<Props> = ({
  dialogState,
  translationPrefix,
  isBulkMode = false,
  terminationDate,
  onDeactivate,
  onPause,
  onUpdate,
  onSuccessReset,
  updateProgress,
  pauseProgress,
  disabled,
}) => {
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();
  const { enqueueSnackbar } = useSnackbar();
  const [updateErrors, setUpdateErrors] = useState<BulkActivationResultItemDTO[]>();
  const [resultDialogData, setResultDialogData] = useState<{ employees: BulkActivationResultItemDTO[]; variant: 'deactivate' | 'pause' }>();

  const benefitActivationDialogExtension = getExtensionForBenefit(benefit, BenefitActivationDialogExtensionInterface);

  const handleAction = useCallback(
    async (mutateFn: () => Promise<BulkActivationUpdateResponseDTO>, variant: 'deactivate' | 'pause' | 'update') => {
      setUpdateErrors(undefined);
      setResultDialogData(undefined);
      try {
        const response = await mutateFn();
        const errorResults = response.results.filter(r => r.status === 'ERROR');

        const successCount = enqueueBulkResultSnackbar(response.results, t, enqueueSnackbar, {
          empty: `${translationPrefix}.noEditable`,
          successFragment: `${translationPrefix}.${variant}Success`,
        });

        // keep the selection around for retry on partial failure - only reset on a fully clean run
        if (successCount && errorResults.length === 0) {
          onSuccessReset?.();
        }

        if (errorResults.length > 0) {
          setUpdateErrors(errorResults);
        }

        let hasAffectedEmployees = false;
        if (variant !== 'update' && (benefit === BenefitDTOBenefitEnum.Bikeleasing || benefit === BenefitDTOBenefitEnum.BikeleasingV2)) {
          const affectedEmployees = response.results.filter(r =>
            variant === 'deactivate' ? r.status === 'ADJUSTMENT' || r.status === 'BLOCKED' : r.status === 'BLOCKED',
          );
          if (affectedEmployees.length > 0) {
            hasAffectedEmployees = true;
            setResultDialogData({ employees: affectedEmployees, variant });
          }
        }

        // the error dialog renders first; closing it reveals the adjustment/blocked dialog instead of losing it
        if (errorResults.length > 0 || hasAffectedEmployees) {
          return;
        }
      } catch {
        // handled by the mutation's own onError
      }
      dialogState.handleClose();
    },
    [dialogState, t, enqueueSnackbar, translationPrefix, benefit, onSuccessReset],
  );

  const handlePause = useCallback(
    async (pauseDTO: PauseMutationDTO): Promise<void> => {
      if (!onPause) {
        return;
      }
      await handleAction(() => onPause(pauseDTO), 'pause');
    },
    [handleAction, onPause],
  );

  const handleDeactivate = useCallback(
    async (updateDTO: UpdateBenefitActivationDTO): Promise<void> => {
      if (!onDeactivate) {
        return;
      }
      await handleAction(() => onDeactivate(updateDTO), 'deactivate');
    },
    [handleAction, onDeactivate],
  );

  const handleUpdate = useCallback(
    async (updateDTO: UpdateBenefitActivationDTO): Promise<void> => {
      if (!onUpdate) {
        return;
      }
      await handleAction(() => onUpdate(updateDTO), 'update');
    },
    [handleAction, onUpdate],
  );

  const EditFormComponent = useBoundActivationComponent(BulkEditForm, benefit);

  const handleCloseError = useCallback(() => {
    setUpdateErrors(undefined);
  }, []);

  const handleCloseResult = useCallback(() => {
    setResultDialogData(undefined);
    dialogState.handleClose();
  }, [dialogState]);

  if (updateErrors) {
    return <BulkErrorDialog dialogState={{ isOpen: !!updateErrors, handleClose: handleCloseError }} updateErrors={updateErrors} />;
  }

  if (resultDialogData) {
    if (isBulkMode) {
      return (
        <BulkActivationResultDialog
          dialogState={{ isOpen: true, handleClose: handleCloseResult }}
          variant={resultDialogData.variant}
          employees={resultDialogData.employees}
        />
      );
    }
    return (
      <SingleActivationResultDialog
        dialogState={{ isOpen: true, handleClose: handleCloseResult }}
        variant={resultDialogData.variant}
        result={resultDialogData.employees[0]}
      />
    );
  }

  return (
    <TabbedDialog dialogState={dialogState} title={t(`${translationPrefix}.title`)} maxWidth="sm" fullWidth>
      {onDeactivate && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.deactivate`)}>
          <DeactivateForm
            onCancel={dialogState.handleClose}
            onUpdate={handleDeactivate}
            terminationDate={terminationDate}
            progress={updateProgress}
            disabled={disabled}
          />
        </TabbedDialogTab>
      )}
      {onPause && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.pauseBenefit`)}>
          <PauseForm onUpdate={handlePause} terminationDate={terminationDate} progress={pauseProgress} disabled={disabled} />
        </TabbedDialogTab>
      )}
      {onUpdate && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.changeOptions`)}>
          <EditFormComponent
            onCancel={dialogState.handleClose}
            onUpdate={handleUpdate}
            ChangeOptionsInfo={benefitActivationDialogExtension?.ChangeOptionsInfo}
            progress={updateProgress}
            disabled={disabled}
          />
        </TabbedDialogTab>
      )}
    </TabbedDialog>
  );
};
